// @givmo/design-system — Switch (a web on/off toggle).
//
// The Flutter kit has no dedicated switch widget (toggles were Material `Switch`es styled by
// the theme's `primary = blue`). This ports that intent to a Base UI Switch
// (@base-ui/react/switch) themed from the 03-3a tokens:
//   - ON track  = blue (#3C6DE5, the kit primary); OFF track = gray40 (#D4D6D8, the kit's
//     light divider gray — the natural "inactive" surface).
//   - White thumb that slides between the two ends.
//   - focus-glow shadow on keyboard focus (consistent with the other controls).
//   - Disabled = 50% opacity + the not-allowed cursor, keyed on Base UI's `data-disabled`. The root is
//     a `<span role="switch">`, which the `:disabled` pseudo-class (the `disabled:` variant) can never
//     match — a `disabled:` recipe painted every disabled switch exactly like an enabled one.
//
// a11y: Base UI's Switch renders an accessible `role="switch"` with a hidden input; pass an
// `aria-label` (or `aria-labelledby`) so it has an accessible name. `testId` forwards to the
// root switch element.
import { Switch as BaseSwitch } from '@base-ui/react/switch';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface SwitchProps extends TestIdProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  /** Form field name (for native form submission). */
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * Visual appearance (added 2026-06-26, ADDITIVE — mirrors Button/Select/Tabs):
   *   `legacy` (default) → the Flutter-ported toggle (h-28 blue track) — unchanged.
   *   `saas`             → the fintech toggle (h-20 brand-600 track, tighter thumb, focus ring).
   * Same Base UI `role="switch"` + a11y in both; only the classes differ.
   */
  appearance?: 'legacy' | 'saas';
}

export function Switch({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  required,
  name,
  className,
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  appearance = 'legacy',
}: SwitchProps) {
  const saas = appearance === 'saas';
  return (
    <BaseSwitch.Root
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      required={required}
      name={name}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={
        saas
          ? cn(
              'relative inline-flex h-20 w-36 shrink-0 cursor-pointer items-center rounded-full p-2 transition-colors',
              // OFF = neutral-300 track; ON = brand-600 track.
              'bg-neutral-300 data-[checked]:bg-brand-600',
              'outline-none focus-visible:shadow-ring',
              'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
              className,
            )
          : cn(
              'relative inline-flex h-[28px] w-[48px] shrink-0 items-center rounded-all100 p-2 transition-colors',
              // OFF = gray40 track; ON = blue track.
              'bg-gray40 data-[checked]:bg-blue',
              'outline-none focus-visible:shadow-focus-glow',
              'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
              className,
            )
      }
      {...testIdProp(testId)}
    >
      <BaseSwitch.Thumb
        className={
          saas
            ? cn(
                'size-16 rounded-full bg-white shadow-xs transition-transform',
                'data-[checked]:translate-x-[16px]',
              )
            : cn(
                'size-[24px] rounded-all100 bg-white shadow-soft transition-transform',
                'data-[checked]:translate-x-[20px]',
              )
        }
      />
    </BaseSwitch.Root>
  );
}
